'use client'

import * as React from 'react'
import { IconUpload } from '@repo/design-system-cf/components/icons'
import { Typography } from '@repo/design-system-cf/components/typography'

const IMAGE_MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
}

// A drop often leaves file.type empty. The accept check requires a MIME type.
export function withInferredImageType(file: File) {
  if (file.type) {
    return file
  }
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  const type = IMAGE_MIME_BY_EXTENSION[extension]
  if (!type) {
    return file
  }
  return new File([file], file.name, { type, lastModified: file.lastModified })
}

export function ToolFileDrop({
  activeLabel,
  disabled = false,
  onFile,
  children,
}: {
  activeLabel: string
  disabled?: boolean
  onFile: (file: File) => void
  children: React.ReactNode
}) {
  const depth = React.useRef(0)
  const [active, setActive] = React.useState(false)

  function isFileDrag(event: React.DragEvent) {
    return Array.from(event.dataTransfer.types).includes('Files')
  }

  function handleDragEnter(event: React.DragEvent) {
    if (!isFileDrag(event)) {
      return
    }
    event.preventDefault()
    if (disabled) {
      return
    }
    depth.current += 1
    setActive(true)
  }

  function handleDragOver(event: React.DragEvent) {
    if (!isFileDrag(event)) {
      return
    }
    event.preventDefault()
    if (!disabled) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  function handleDragLeave(event: React.DragEvent) {
    if (!isFileDrag(event) || disabled) {
      return
    }
    event.preventDefault()
    depth.current = Math.max(0, depth.current - 1)
    if (depth.current === 0) {
      setActive(false)
    }
  }

  function handleDrop(event: React.DragEvent) {
    if (!isFileDrag(event)) {
      return
    }
    event.preventDefault()
    depth.current = 0
    setActive(false)
    if (disabled) {
      return
    }
    const next = event.dataTransfer.files[0]
    if (!next) {
      return
    }
    onFile(withInferredImageType(next))
  }

  return (
    <div
      className="relative h-full"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {active ? (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-cf-purple-900/80">
          <div className="rounded-2 bg-cf-white p-6 text-center">
            <IconUpload className="mx-auto mb-2 size-12 text-cf-purple-900" />
            <Typography variant="body-01">{activeLabel}</Typography>
          </div>
        </div>
      ) : null}
    </div>
  )
}
